import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, PhoneForwarded } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getAiCallerStatus, aiCallerTestCall, aiHandoverDone } from "@/lib/api";

// AI lead caller pilot (backend routes/ai_caller.py): the "asked for a person" alert on the board, and the
// pilot panel (AI vs reps, recent AI calls, a test call to your own phone) on the Assistant tab.

const err = (e, fallback) => e?.response?.data?.detail || fallback;
const time = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");

export function AiHandoffs({ items }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-2" data-testid="ai-handoffs">
      {items.map((h) => (
        <div key={h.lead_id + h.at} className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm flex gap-2">
          <PhoneForwarded className="h-4 w-4 mt-0.5 shrink-0 text-amber-300" />
          <div>
            <div className="font-semibold text-amber-100">
              {h.name} asked the AI caller for a person · {h.gym} · {time(h.at)}
            </div>
            <div className="text-zinc-300">
              {h.state === "transfer" ? `Being put through to ${h.rep_name?.split(" ")[0] || "the rep on shift"}.` : "Nobody was on shift: in the call-back list."}
              {h.reason ? ` Why: ${h.reason}.` : ""} {h.summary}
            </div>
            <a href={`tel:${h.phone}`} className="text-amber-300 hover:text-amber-200 text-xs">{h.phone}</a>
          </div>
        </div>
      ))}
    </div>
  );
}

const KIND = { person: "Asked for a person", callback: "Call back", placeholder: "Placeholder booking",
  before_consult: "Booked: call before the consult" };
const KIND_STYLE = { person: "text-amber-300", callback: "text-sky-300", placeholder: "text-violet-300",
  before_consult: "text-emerald-300" };

// When to call, said plainly: "Now", "Due now (since 4:10 PM)", "6 PM tomorrow".
function callWhen(iso) {
  if (!iso) return "Now";
  const at = new Date(iso);
  const now = new Date();
  if (at <= now) return now - at < 5 * 60000 ? "Now" : `Due now (since ${at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })})`;
  const day = new Date(now); day.setDate(day.getDate() + 1);
  const label = at.toDateString() === now.toDateString() ? "today"
    : at.toDateString() === day.toDateString() ? "tomorrow" : at.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
  return `${at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} ${label}`;
}

// The board's first column while the AI caller pilot runs: every lead the AI passed to the team and when to call.
// A card leaves once a rep calls the lead from FPA, or someone presses Done.
export function AiHandoverColumn({ items, onChanged }) {
  const done = async (h) => {
    try { await aiHandoverDone(h.lead_id); toast.success("Taken off the AI handover column"); onChanged?.(); }
    catch (e) { toast.error(err(e, "Couldn't update it")); }
  };
  return (
    <section className="card-surface shrink-0 w-[86vw] max-w-[320px] sm:w-[320px] snap-start flex flex-col min-w-0 border-amber-500/30"
      data-testid="ai-handover-column">
      <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-2 border-b border-[#1a1a1a]">
        <div className="flex items-center gap-1.5 min-w-0">
          <Bot className="h-4 w-4 text-amber-300 shrink-0" />
          <div className="font-semibold text-sm">AI handover</div>
        </div>
        <span className={`text-xs shrink-0 ${items.length ? "text-amber-300" : "text-zinc-500"}`}>{items.length} to call</span>
      </div>
      <div className="p-2 space-y-2 overflow-y-auto max-h-[calc(100dvh-300px)] min-h-[160px]">
        {items.length === 0 && <div className="text-xs text-zinc-600 px-1 py-3">Nothing from the AI caller right now</div>}
        {items.map((h) => {
          const due = !h.call_at || new Date(h.call_at) <= new Date();
          return (
            <div key={h.lead_id} className={`rounded-lg border px-3 py-2 text-sm ${due ? "border-amber-500/50 bg-amber-500/10" : "border-[#262626] bg-[#111]"}`}
              data-testid={`ai-handover-${h.lead_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-zinc-100 truncate">{h.name || "Lead"}</div>
                <div className="text-[10px] text-zinc-500 shrink-0">{(h.gym || "").split(" - ").pop()}</div>
              </div>
              <div className={`text-[11px] ${KIND_STYLE[h.kind] || "text-zinc-300"}`}>{KIND[h.kind] || h.kind}</div>
              <div className={`mt-1 text-xs font-semibold ${due ? "text-amber-200" : "text-sky-200"}`} data-testid={`ai-handover-when-${h.lead_id}`}>
                Call: {callWhen(h.call_at)}
              </div>
              <div className="mt-1 text-xs text-zinc-300">{h.why}</div>
              {h.detail && <div className="mt-0.5 text-xs text-zinc-400">{h.detail}</div>}
              <div className="mt-1.5 flex items-center justify-between gap-2">
                <a href={`tel:${h.phone}`} className="text-amber-300 hover:text-amber-200 text-xs inline-flex items-center gap-1">
                  <PhoneForwarded className="h-3 w-3" />{h.phone}
                </a>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-zinc-400" onClick={() => done(h)}
                  data-testid={`ai-handover-done-${h.lead_id}`}>Done</Button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-3 pb-2 text-[10px] text-zinc-600">Call from the lead&apos;s card in its gym column so the call is logged; it then leaves this list.</div>
    </section>
  );
}

const MODE = { off: "Off", test: "Test calls only", on: "Live: calling new leads" };
const pct = (a, b) => (b ? `${Math.round((100 * a) / b)}%` : "–");

export function AiCallerPanel() {
  const [data, setData] = useState(null);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [engine, setEngine] = useState("");
  const load = useCallback(() => getAiCallerStatus().then(setData).catch(() => setData(null)), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  if (!data) return null;
  const test = async () => {
    setBusy(true);
    try { await aiCallerTestCall(phone, name || "Test", engine); toast.success("Calling now. Pick up and talk to it like a lead."); load(); }
    catch (e) { toast.error(err(e, "Couldn't place the test call")); }
    setBusy(false);
  };
  const rows = [["Leads", "leads"], ["Consults booked", "booked"], ["Showed up", "showed"], ["No-show", "no_show"]];
  return (
    <section className="card-surface p-5 space-y-4" data-testid="ai-caller-panel">
      <div className="flex items-start gap-2">
        <Bot className="h-5 w-5 text-emerald-300 mt-0.5" />
        <div>
          <h3 className="font-display text-lg font-bold">AI lead caller (pilot)</h3>
          <p className="text-sm text-zinc-400">
            {MODE[data.mode]}{data.ready ? "" : " · no voice engine is set up yet"}. New leads at the pilot gym are split {Math.round(data.share * 100)}% AI,
            the rest reps. Judge it on consults that show up, not bookings.
          </p>
        </div>
      </div>
      <table className="text-sm w-full max-w-md">
        <thead><tr className="text-zinc-500 text-left"><th className="font-normal" /><th className="font-normal">AI</th><th className="font-normal">Reps</th></tr></thead>
        <tbody>
          {rows.map(([label, k]) => (
            <tr key={k}><td className="text-zinc-400 py-0.5">{label}</td><td className="tabular-nums">{data.arms.ai[k]}</td><td className="tabular-nums">{data.arms.rep[k]}</td></tr>
          ))}
          <tr><td className="text-zinc-400 py-0.5">Show rate (of leads)</td><td>{pct(data.arms.ai.showed, data.arms.ai.leads)}</td><td>{pct(data.arms.rep.showed, data.arms.rep.leads)}</td></tr>
        </tbody>
      </table>
      {data.engine_ready?.elevenlabs && (
        <div className="text-sm space-y-1" data-testid="ai-caller-engines">
          <div className="eyebrow">Sarvam vs ElevenLabs · {Math.round((data.elevenlabs_share || 0) * 100)}% of the AI's leads on ElevenLabs</div>
          <table className="w-full max-w-md">
            <thead><tr className="text-zinc-500 text-left"><th className="font-normal" /><th className="font-normal">Sarvam</th><th className="font-normal">ElevenLabs</th></tr></thead>
            <tbody>
              {[["Leads", "leads"], ["Reached", "reached"], ["Booked", "booked"], ["Showed up", "showed"]].map(([label, k]) => (
                <tr key={k}><td className="text-zinc-400 py-0.5">{label}</td>
                  <td className="tabular-nums">{data.engines?.sarvam?.[k] ?? 0}</td><td className="tabular-nums">{data.engines?.elevenlabs?.[k] ?? 0}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.recent.length > 0 && (
        <div className="space-y-1 text-sm">
          <div className="eyebrow">Recent AI calls</div>
          {data.recent.slice(0, 15).map((r) => (
            <div key={r.id} className="flex flex-wrap gap-x-2 text-zinc-300">
              <span className="text-zinc-500">{time(r.at)}</span>
              <span>{r.name}{r.test ? " (test)" : ""}</span>
              {r.engine && data.engine_ready?.elevenlabs && <span className="text-zinc-500">{r.engine === "elevenlabs" ? "ElevenLabs" : "Sarvam"}</span>}
              <span className="text-zinc-400">{r.booked ? `booked ${r.booked}` : r.outcome || r.state}</span>
              {r.handoff && <span className="text-amber-300">asked for a person</span>}
            </div>
          ))}
        </div>
      )}
      {data.mode !== "off" && data.ready && (
        <div className="flex flex-wrap items-end gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name to use" className="w-36 bg-[#0a0a0a] border-[#262626]" />
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your number" inputMode="tel" className="w-44 bg-[#0a0a0a] border-[#262626]" />
          {data.engine_ready?.elevenlabs && data.engine_ready?.sarvam && (
            <select value={engine} onChange={(e) => setEngine(e.target.value)} className="h-9 rounded-md bg-[#0a0a0a] border border-[#262626] px-2 text-sm" data-testid="ai-caller-engine">
              <option value="">Either engine</option><option value="sarvam">Sarvam</option><option value="elevenlabs">ElevenLabs</option>
            </select>
          )}
          <Button onClick={test} disabled={busy || !phone} className="rounded-full">Test call to my phone</Button>
          <span className="text-xs text-zinc-500">Only numbers on the test list. Nothing gets booked.</span>
        </div>
      )}
    </section>
  );
}
